import { Matrix3, Vector2 } from "three";
/** Target angle for each segment of tessellated arc. */
export const arcTessellationAngle = (10 / 180) * Math.PI;
/** Generate vertices for arc segment.
 *
 * @param vertices Generated vertices pushed here.
 * @param center {{x, y}} Center vector.
 * @param radius {number}
 * @param startAngle {?number} Start angle. Zero if not specified. Arc is drawn in CCW direction
 *  from start angle towards end angle.
 * @param endAngle {?number} Optional end angle. Full circle is drawn if not specified.
 * @param tessellationAngle {?number} Arc tessellation angle, default value is taken from scene
 *  options.
 * @param yRadius {?number} Specify to get ellipse arc. `radius` parameter used as X radius.
 * @param transform {?Matrix3} Optional transform matrix for the arc. Applied as last operation.
 */
export function generateArcVertices(props) {
    let { center, radius, startAngle, endAngle, tessellationAngle, yRadius, transform, dxfHeaders } = props;
    const vertices = [];
    const angDir = dxfHeaders["$ANGDIR"] || 0;
    const angBase = dxfHeaders["ANGBASE"] || 0;
    if (!center || !radius) {
        return [];
    }
    if (!tessellationAngle) {
        tessellationAngle = arcTessellationAngle;
    }
    if (!yRadius) {
        yRadius = radius;
    }
    /* Normalize angles - make them starting from +X in CCW direction. End angle should be
     * greater than start angle.
     */
    if (!startAngle || !startAngle) {
        startAngle = 0;
    }
    else {
        startAngle += angBase;
    }
    let isClosed = false;
    if (!endAngle || !endAngle) {
        endAngle = startAngle + 2 * Math.PI;
        isClosed = true;
    }
    else {
        endAngle += angBase;
    }
    if (angDir) {
        const tmp = startAngle;
        startAngle = endAngle;
        endAngle = tmp;
    }
    while (endAngle <= startAngle) {
        endAngle += Math.PI * 2;
    }
    const arcAngle = endAngle - startAngle;
    let numSegments = Math.floor(arcAngle / tessellationAngle) * 4;
    if (numSegments === 0) {
        numSegments = 1;
    }
    const step = arcAngle / numSegments;
    for (let i = 0; i <= numSegments; i++) {
        if (i === numSegments && isClosed) {
            break;
        }
        const a = startAngle + i * step;
        const v = new Vector2(radius * Math.cos(a), (yRadius || 0) * Math.sin(a));
        v.add(new Vector2(center.x, center.y));
        if (transform) {
            v.applyMatrix3(transform);
        }
        vertices.push({ x: v.x, y: v.y });
    }
    return vertices;
}
/** Check extrusionDirection property of the entity and return corresponding transform matrix.
 *
 * @return {?Matrix3} Null if not transform required.
 */
export function getEntityExtrusionTransform(entity) {
    //XXX For now just mirror X axis if extrusion Z is negative. No full support for arbitrary
    // OCS yet.
    if (!entity.hasOwnProperty("extrusionDirection")) {
        return undefined;
    }
    if (entity.extrusionDirection && entity.extrusionDirection.z > 0) {
        return undefined;
    }
    return new Matrix3().scale(-1, 1);
}
//# sourceMappingURL=helpers.js.map